import type { BillingReportRow } from '@/pages/BillingReportResults/types';
import { findRowFlagFilterOption } from '@/pages/BillingReportResults/utils/rowFlagFilterOptions';
import {
  DATE_ROW_FIELDS,
  NUMERIC_ROW_FIELDS,
  REAL_ALIAS_ROW_FIELDS,
  SERVER_SORTABLE_ROW_FIELDS,
  TEXT_ROW_FIELDS
} from '@/pages/BillingReportResults/utils/serverRowFields';
import {
  type FilterModel,
  type GridFieldConfig,
  type SingleCondition,
  type SingleFilter,
  type SortModelItem,
  isBlank,
  matchesFieldCondition,
  rowMatchesFilterModelWith,
  sortModelFromSearchWith,
  translateFieldCondition,
  translateFilterModelWith,
  translateSortModelWith
} from '@/utils/gridFilters';

export type { FilterModel, SortModelItem } from '@/utils/gridFilters';
export {
  filterModelFromSearch,
  gridStateToSearch,
  pageFromSearch,
  pageToSearch
} from '@/utils/gridFilters';

/**
 * A "show me the items with this alert" filter, driven by the Alert Summary
 * panel. Not part of the grid's filter model: the `errors`/`warnings` columns are
 * dict-shaped and excluded from the server's filter whitelist
 * (billing_item_row_fields.py), so the narrowing goes through the relational
 * alert tables by BillingAlert id instead.
 */
export interface AlertFilter {
  alertId: number;
  description: string;
}

/**
 * Query params for an AlertFilter.
 *
 * The exclusions matter: the summary endpoint leaves out items that are already
 * submitted or marked non-billable (the legacy panel's behaviour, and its
 * defaults), so without asking for the same scope here, clicking a message
 * counted as 30 would return more than 30 rows. `alert_ids` itself already
 * skips ignored alerts server-side, matching how the summary counts them.
 */
export const alertFilterParams = (alertFilter: AlertFilter | null): Record<string, string> => {
  if (!alertFilter) return {};

  return {
    alert_ids: String(alertFilter.alertId),
    has_claim_generated: 'false',
    non_billable: 'false'
  };
};

// The alert filter's own URL params. Named separately from the API's `alert_ids`
// so the URL stays readable, and so restoring one can't collide with a param
// meant for the request.
const ALERT_ID_PARAM = 'alert_id';
const ALERT_LABEL_PARAM = 'alert_label';

/**
 * Reads an AlertFilter back out of a URL query string, so filtering by an alert
 * survives a reload and can be shared as a link.
 *
 * The label is carried along purely so the filter chip can name the alert
 * without waiting on the summary request; the id is what actually filters, so a
 * link whose label has since been reworded still narrows to the right items.
 */
export const alertFilterFromSearch = (search: string): AlertFilter | null => {
  const params = new URLSearchParams(search);
  const alertId = Number(params.get(ALERT_ID_PARAM));

  if (!Number.isInteger(alertId) || alertId <= 0) return null;

  return {
    alertId,
    description: params.get(ALERT_LABEL_PARAM) || `Alert #${alertId}`
  };
};

/**
 * Writes `alertFilter` into a URL query string, replacing whatever alert filter
 * it held and leaving every other param untouched. A null filter clears it.
 */
export const alertFilterToSearch = (search: string, alertFilter: AlertFilter | null): string => {
  const params = new URLSearchParams(search);
  [ALERT_ID_PARAM, ALERT_LABEL_PARAM].forEach((param) => params.delete(param));

  if (alertFilter) {
    params.set(ALERT_ID_PARAM, String(alertFilter.alertId));
    params.set(ALERT_LABEL_PARAM, alertFilter.description);
  }

  return params.toString();
};

// Deep-link param for narrowing straight to a single billing item (e.g. a
// link from an external system) -- separate from the `filters` grid-state
// param so a plain `?billing_item_id=<uuid>` link stays readable and doesn't
// require callers to hand-encode a filter model.
const BILLING_ITEM_ID_PARAM = 'billing_item_id';

/**
 * Reads a `billing_item_id` deep-link param out of a URL query string into
 * an AG Grid filter-model entry for the (hidden) billing_item_id column, so
 * it can be merged into the restored filter model on mount the same way
 * handleClaimLoad sets person_id.
 */
export const billingItemIdFilterFromSearch = (search: string): FilterModel => {
  const billingItemId = new URLSearchParams(search).get(BILLING_ITEM_ID_PARAM);
  if (!billingItemId) return {};

  return {
    billing_item_id: { filterType: 'text', type: 'equals', filter: billingItemId }
  };
};

// The billing grid's server-filterable columns, mirroring billing_item_filter.py's
// GridFilterSetBuilder calls. Blank checks cover date row fields too; the
// service_date real-field alias is date-filterable but has no blank check.
const BILLING_GRID_FIELDS: GridFieldConfig = {
  text: TEXT_ROW_FIELDS,
  date: new Set([...DATE_ROW_FIELDS, ...REAL_ALIAS_ROW_FIELDS]),
  number: NUMERIC_ROW_FIELDS,
  blank: new Set([...TEXT_ROW_FIELDS, ...DATE_ROW_FIELDS]),
  sortable: SERVER_SORTABLE_ROW_FIELDS
};

/**
 * Translates AG Grid's sort model into the `ordering` query param supported
 * by BillingItemViewSet.list. Fields the backend can't sort by are excluded
 * from the grid's sortable columns in the first place (see columnDefs.tsx's
 * serverSide option), but we defensively drop them here too.
 */
export const translateSortModel = (sortModel: SortModelItem[]): string | undefined =>
  translateSortModelWith(BILLING_GRID_FIELDS.sortable, sortModel);

/**
 * Id columns filtered by exact match rather than the row fields' icontains --
 * hidden columns set programmatically or from a URL param (person_id by
 * handleClaimLoad, billing_item_id by a deep link), not by the grid's own
 * filter UI. Each has a same-named exact-match filter on the server
 * (billing_item_filter.py), so adding one here is a one-line change.
 */
const EXACT_MATCH_ID_FIELDS = new Set(['person_id', 'billing_item_id']);

const BLANK_FILTER_TYPES = new Set(['blank', 'notBlank']);

// A single filter condition's query params. The billing-only operators --
// row-flag "quick filter" segments, the dict-shaped errors column, exact-match
// ids and the list-shaped indicators column -- are handled here; every other
// column goes through the shared row-field translation.
const translateCondition = (field: string, filter: SingleCondition): Record<string, string> => {
  const rowFlagOption = findRowFlagFilterOption(field, filter.type);
  if (rowFlagOption) return { ...rowFlagOption.params };

  if (field === 'errors' && filter.type && BLANK_FILTER_TYPES.has(filter.type)) {
    return { errors_isnull: filter.type === 'blank' ? 'true' : 'false' };
  }

  if (EXACT_MATCH_ID_FIELDS.has(field) && filter.type === 'equals' && filter.filter) {
    return { [field]: String(filter.filter) };
  }

  if (field === 'indicators' && filter.filter) {
    if (filter.type === 'contains') return { indicators_contains: String(filter.filter) };
    if (filter.type === 'notContains') return { indicators_not_contains: String(filter.filter) };
    return {};
  }

  return translateFieldCondition(BILLING_GRID_FIELDS, field, filter);
};

/**
 * Translates AG Grid's filter model into the query params supported by
 * BillingItemViewSet.list -- text fields as `<field>` (icontains), date
 * fields (including the service_date real-field alias) as
 * `<field>_from`/`<field>_to`, numeric fields as `<field>_min`/`<field>_max`,
 * the "quick filter" operators (blank/notBlank, on_hold, indicators
 * contains/notContains) used by BillingReportFilters segments, and
 * person_id/billing_item_id as exact-match filters (person_id set
 * programmatically, e.g. by handleClaimLoad; billing_item_id restored from
 * the `billing_item_id` URL param for deep links to a single item).
 * Two-condition column filters go in `condition_groups` instead.
 */
export const translateFilterModel = (filterModel: FilterModel): Record<string, string> =>
  translateFilterModelWith(translateCondition, filterModel);

/**
 * Evaluates a single ag-grid filter condition against a row's own field value.
 * Mirrors the semantics of translateFilterModel (and the server-side
 * equivalents it targets), but runs entirely client-side against data already
 * held in memory -- used to tell whether a *selected* row would still be
 * included by a newly-applied filter, without a round-trip to the server.
 * Unrecognized field/filter combinations return true (assume still visible)
 * so we never incorrectly drop a valid selection.
 */
const rowMatchesCondition = (
  row: BillingReportRow,
  field: string,
  filter: SingleFilter
): boolean => {
  const rowFlagOption = findRowFlagFilterOption(field, filter.type);
  if (rowFlagOption) return rowFlagOption.matches(row);

  if (field === 'errors' && filter.type === 'blank') return isBlank(row.errors);
  if (field === 'errors' && filter.type === 'notBlank') return !isBlank(row.errors);

  const rawValue = (row as unknown as Record<string, unknown>)[field];

  if (EXACT_MATCH_ID_FIELDS.has(field) && filter.type === 'equals') {
    return rawValue === filter.filter;
  }

  if (field === 'indicators') {
    const indicators = row.indicators || [];
    if (filter.type === 'contains') return indicators.includes(String(filter.filter));
    if (filter.type === 'notContains') return !indicators.includes(String(filter.filter));
    return true;
  }

  return matchesFieldCondition(BILLING_GRID_FIELDS, rawValue, field, filter);
};

/**
 * Whether `row` would still satisfy every field in `filterModel` -- used to
 * clear selection on rows that drop out of view when the user changes/adds a
 * filter. See rowMatchesCondition for the per-field matching logic.
 */
export const rowMatchesFilterModel = (row: BillingReportRow, filterModel: FilterModel): boolean =>
  rowMatchesFilterModelWith(rowMatchesCondition, row, filterModel);

/**
 * Reads a sort model out of a URL query string. Columns the server can't sort
 * by are dropped, matching translateSortModel.
 */
export const sortModelFromSearch = (search: string): SortModelItem[] =>
  sortModelFromSearchWith(BILLING_GRID_FIELDS.sortable, search);
